import React from 'react';
import { useAsyncValue } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Components
import RouteChildOrders from '../../components/RouteChildOrders';

// Hooks
import { useAuth } from '../../../../../hooks/useAuth';
import { useResolvedPromise } from '../../../../../hooks/useResolvedPromise';

// Utils
import {
  canAttachSegmentPhotos,
  canCreateBasicActionInRoute,
  canLinkOrdersToRoute,
  canOpenSegmentEditor,
  segmentEditorMode,
} from '../../../../../utils/surveillanceRoute';

// Types
import { SurveillanceRoute } from '../../../../../types/surveillanceRoute';

// Styled
import { Container, EmptyState } from './styles';

interface LoaderData {
  route: SurveillanceRoute;
  /** Lo que trae el servidor cuando llega; `null` = no se pudo refrescar. */
  fresh?: Promise<LoaderData | null>;
}

export default function RouteChildOrdersPage(): JSX.Element {
  const { t } = useTranslation('pages');
  // El loader sirve el SNAPSHOT de la caché al instante y el servidor llega
  // después en `fresh` (spec 2026-09-11). La Vista previa ya lo consumía;
  // aquí no, y tras una recarga la pestaña se quedaba con lo cacheado — las
  // OTs vinculadas desde otra sesión (cross-link 3) no aparecían hasta
  // navegar. El servidor manda en cuanto llega; si no llega, se queda el
  // snapshot.
  const snapshot = useAsyncValue() as LoaderData;
  const freshData = useResolvedPromise(snapshot?.fresh);
  const loaderData = (freshData ?? snapshot) as LoaderData;
  const route = loaderData?.route;
  // Misma forma que usa la Vista previa para el dueño del cronómetro.
  const currentUser = useAuth()?.user?.user;

  // Quién puede añadir fotos a los tramos se decide AQUÍ, que es donde está el
  // recorrido entero (equipo y dueño del cronómetro). La card solo obedece,
  // igual que con el lápiz de corregir.
  const canAttachPhotos = canAttachSegmentPhotos(currentUser, route);

  // Corregir el tramo se decide AQUÍ por el mismo motivo que las fotos: desde
  // 2026-08-15 la regla mira el EQUIPO del recorrido y el dueño del cronómetro,
  // y a la card solo le llega el estado. Antes lo calculaba ella con el rol,
  // que ya no alcanza.
  const canEditSegments = canOpenSegmentEditor(currentUser, route);
  const editorMode = segmentEditorMode(currentUser, route) ?? 'management';

  // Crear una básica y vincular a un tramo (2026-09-04): para la tablet es la
  // misma regla que el cronómetro (su equipo y, con la patrulla viva, su
  // dueña); gestión siempre. Se decide aquí, donde está el recorrido entero,
  // y la card obedece — como con las fotos y el lápiz. Antes bastaba el rol y
  // el estado, y una tablet colgaba la básica del recorrido de otra patrulla.
  const canCreateBasicAction = canCreateBasicActionInRoute(currentUser, route);
  // Cross-link 3: gestión y sala siempre; la tablet si opera el cronómetro.
  const canLinkOrders = canLinkOrdersToRoute(currentUser, route);

  // V4 (Fase C): patrón entidad doble `orderIDs[]` (invariante length === 1).
  const ownOrderId = route?.orderIDs?.[0];
  return (
    <Container>
      {!ownOrderId && (
        <EmptyState>{t('surveillance.childOrders.noOrder')}</EmptyState>
      )}
      {route && (
        <RouteChildOrders
          orderId={ownOrderId}
          routeId={route?.id || ''}
          state={route?.state as string}
          roadId={route?.roadIDs?.[0]}
          roadIds={route?.roadIDs ?? []}
          canAttachPhotos={canAttachPhotos}
          canEditSegments={canEditSegments}
          editorMode={editorMode}
          canCreateBasicAction={canCreateBasicAction}
          linkedExternalOrders={route.linkedExternalOrders ?? []}
          canLinkOrders={canLinkOrders}
          serverRevision={freshData}
        />
      )}
    </Container>
  );
}
